/**
 * File Utilities for Content Block Creation
 *
 * Functions to convert files to content blocks for upload.
 *
 * @module lib/storage/file-utils
 */

import { toast } from "sonner";
import {
  getFileUploadError,
  FileUploadError,
} from "@/constants/toast-messages";
import { SUPPORTED_FILE_TYPES, isSupportedImageType } from "@/constants";
import {
  generateImageThumbnail,
  generatePdfThumbnail,
} from "@/lib/thumbnail-generator";
import type { Base64ContentBlock } from "@/features/chat/types/messages";

// =============================================================================
// FILE CONVERSION
// =============================================================================

/**
 * Convert a File to base64 string
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export interface FileToContentBlockOptions {
  /** Photo date extracted from EXIF (ISO 8601 string) */
  photoDate?: string;
}

/**
 * Convert a File to an internal Base64ContentBlock for images or PDFs.
 * Generates thumbnails for instant preview.
 *
 * @param file - File to convert
 * @param options - Optional metadata like photoDate
 * @returns Promise of Base64ContentBlock
 * @throws Error if file type is not supported
 */
export async function fileToContentBlock(
  file: File,
  options?: FileToContentBlockOptions
): Promise<Base64ContentBlock> {
  if (!SUPPORTED_FILE_TYPES.includes(file.type as typeof SUPPORTED_FILE_TYPES[number])) {
    toast.error(
      getFileUploadError(FileUploadError.UNSUPPORTED_TYPE, {
        fileType: file.type,
        supportedTypes: SUPPORTED_FILE_TYPES.join(", "),
      })
    );
    return Promise.reject(new Error(`Unsupported file type: ${file.type}`));
  }

  const data = await fileToBase64(file);

  if (isSupportedImageType(file.type)) {
    // Generate thumbnail for instant preview
    let thumbnail: string | undefined;
    try {
      thumbnail = await generateImageThumbnail(file);
    } catch (error) {
      console.warn("Failed to generate image thumbnail:", error);
      // Continue without thumbnail - graceful degradation
    }

    // Internal image format for UI
    return {
      type: "image",
      source_type: "base64",
      mime_type: file.type,
      data,
      thumbnail,
      metadata: {
        name: file.name,
        size: file.size,
        // Include photo date if extracted from EXIF
        ...(options?.photoDate && { photoDate: options.photoDate }),
      },
    };
  }

  // PDF - Generate thumbnail from first page
  let thumbnail: string | undefined;
  try {
    thumbnail = await generatePdfThumbnail(file);
  } catch (error) {
    console.warn("Failed to generate PDF thumbnail:", error);
    // Continue without thumbnail - graceful degradation
  }

  // PDF - Internal file format for UI
  return {
    type: "file",
    source_type: "base64",
    mime_type: "application/pdf",
    data,
    thumbnail,
    metadata: {
      filename: file.name,
      name: file.name,
      size: file.size,
    },
  };
}
